import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Autocomplete, MenuItem, Stack, TextField } from "@mui/material";
import api from "services/api";

const EMPTY = { warehouse_code: "", group_name: "", pvm_code: "PVM1", xml_type: "quantitative" };
const label = (item) =>
  `${item.warehouse_code} · ${item.group_name} · ${item.pvm_code} · ${
    item.xml_type === "summary" ? "Suminis" : "Kiekinis"
  }`;

export default function ExportVariantSelector({
  companyId,
  value,
  onChange,
  pvmCodeOptions,
  disabled,
}) {
  const [options, setOptions] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setOptions(null);
    setError("");
    api
      .get("/api/suppliers/options/", { params: { company_id: companyId } })
      .then(({ data }) => {
        if (active) setOptions(data);
      })
      .catch(() => {
        if (active) setError("Nepavyko įkelti tiekėjų variantų.");
      });
    return () => {
      active = false;
    };
  }, [companyId]);
  const mode = value === null ? "keep" : value.mode;
  const variant = value?.variant || EMPTY;
  const update = (key, fieldValue) =>
    onChange({ mode: "new", variant: { ...variant, [key]: fieldValue } });
  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <TextField
        select
        fullWidth
        size="small"
        label="Variantas visiems eksportuojamiems dokumentams"
        value={mode}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value;
          onChange(
            next === "keep" ? null : { mode: next, variant: next === "new" ? { ...EMPTY } : null }
          );
        }}
      >
        <MenuItem value="keep">Palikti dokumentų nustatymus</MenuItem>
        <MenuItem value="existing">Pasirinkti esamą variantą</MenuItem>
        <MenuItem value="new">Pridėti naują variantą</MenuItem>
      </TextField>
      {error && <Alert severity="error">{error}</Alert>}
      {mode !== "keep" && (
        <>
          <Autocomplete
            options={options?.combinations || []}
            getOptionLabel={label}
            loading={!options && !error}
            noOptionsText="Esamų variantų nėra"
            loadingText="Įkeliama..."
            disabled={disabled}
            value={mode === "existing" ? value.variant : null}
            isOptionEqualToValue={(a, b) => label(a) === label(b)}
            onChange={(_, chosen) =>
              onChange({ mode, variant: chosen || (mode === "new" ? { ...EMPTY } : null) })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={mode === "new" ? "Kopijuoti nustatymus iš kito tiekėjo" : "Esamas variantas"}
              />
            )}
          />
          {(mode === "new" || value.variant) && (
            <>
              {[
                ["warehouse_code", "Sandėlio kodas", "warehouse_codes"],
                ["group_name", "Grupės pavadinimas", "group_names"],
              ].map(([key, title, list]) => (
                <Autocomplete
                  key={key}
                  freeSolo
                  options={options?.[list] || []}
                  value={variant[key]}
                  disabled={disabled}
                  onInputChange={(_, text, reason) => {
                    if (reason === "input" || reason === "clear") update(key, text);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      size="small"
                      label={title}
                      inputProps={{ ...params.inputProps, maxLength: 255 }}
                    />
                  )}
                />
              ))}
              <TextField
                select
                required
                size="small"
                label="PVM kodas"
                value={variant.pvm_code}
                disabled={disabled}
                onChange={(e) => update("pvm_code", e.target.value)}
              >
                {pvmCodeOptions
                  .filter((item) => item.value)
                  .map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField
                select
                size="small"
                label="XML tipas"
                value={variant.xml_type}
                disabled={disabled}
                onChange={(e) => update("xml_type", e.target.value)}
              >
                <MenuItem value="quantitative">Kiekinis</MenuItem>
                <MenuItem value="summary">Suminis</MenuItem>
              </TextField>
            </>
          )}
          <Alert severity="info">
            Variantas bus išsaugotas kiekvienam pažymėtų dokumentų tiekėjui ir priskirtas tik
            pažymėtiems dokumentams.
          </Alert>
        </>
      )}
    </Stack>
  );
}
ExportVariantSelector.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  pvmCodeOptions: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
};

export function isExportVariantValid(value) {
  return (
    value === null ||
    !!(
      value.variant &&
      ["warehouse_code", "group_name", "pvm_code", "xml_type"].every((key) =>
        value.variant[key]?.trim()
      )
    )
  );
}
